import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Heart, Menu, X } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { SearchDialog } from "@/components/SearchDialog";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Shop Planner", to: "/shop" },
  { label: "Digital Planner", to: "/digital-planner" },
  { label: "Bulk", to: "/bulk" },
  { label: "Inside", to: "/inside-the-planner" },
  { label: "About", to: "/about" },
  { label: "Help", to: "/help" },
];

const initials = (user) => {
  if (!user) return "";
  const src = user.displayName || user.email || "";
  return src
    .split(/[\s@.]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
};

export const Header = () => {
  const { count } = useCart();
  const { count: wCount } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <>
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 bg-cream/90 backdrop-blur-md border-b border-line"
      >
        <div className="container-nc flex items-center justify-between h-16 md:h-20 gap-3">
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden text-espresso"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>

          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-3 text-espresso"
          >
            <span className="relative top-1.5 h-9 w-9 md:h-10 md:w-10 overflow-hidden flex shrink-0 items-center justify-center">
              <img
                src="/favicon.png"
                alt="Note Console logo"
                className="h-full w-full object-cover scale-[2.1]"
              />
            </span>
            <span className="font-display text-2xl md:text-[27px] tracking-wide leading-none">
              Note Console
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 lg:gap-10 flex-1 justify-center">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors relative py-2 ${
                    isActive ? "text-espresso" : "text-muted_brown hover:text-espresso"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {n.label}
                    {isActive && (
                      <span className="absolute left-0 right-0 -bottom-0.5 h-px bg-gold" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3 text-espresso">
            {/* Search pill */}
            <button
              data-testid="search-btn"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="w-9 h-9 rounded-full border border-line hover:border-espresso transition-colors flex items-center justify-center"
            >
              <Search size={15} strokeWidth={1.5} />
            </button>

            {/* Wishlist */}
            <button
              data-testid="wishlist-header-btn"
              onClick={() => navigate("/wishlist")}
              aria-label="Wishlist"
              className="w-9 h-9 rounded-full border border-line hover:border-espresso transition-colors flex items-center justify-center relative"
            >
              <Heart size={15} strokeWidth={1.5} />
              {wCount > 0 && (
                <span
                  data-testid="wishlist-count"
                  className="absolute -top-1 -right-1 bg-gold text-espresso text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-medium"
                >
                  {wCount}
                </span>
              )}
            </button>

            {/* Account initials pill or login */}
            {user ? (
              <button
                data-testid="account-pill"
                onClick={() => navigate("/dashboard")}
                className="w-9 h-9 rounded-full bg-champagne text-espresso text-xs font-medium flex items-center justify-center hover:bg-sand transition-colors uppercase"
              >
                {initials(user) || "•"}
              </button>
            ) : (
              <button
                data-testid="login-pill"
                onClick={() => navigate("/login")}
                className="hidden sm:inline-flex text-xs uppercase tracking-[0.15em] text-espresso border border-line rounded-full px-4 py-2 hover:border-espresso transition-colors"
              >
                Sign In
              </button>
            )}

            {/* Cart pill */}
            <button
              data-testid="cart-btn"
              onClick={() => navigate("/cart")}
              aria-label="Cart"
              className="relative flex items-center gap-2 bg-espresso text-cream rounded-full px-4 h-9 text-xs uppercase tracking-[0.15em] hover:bg-espresso-dark transition-colors"
            >
              <ShoppingBag size={13} strokeWidth={1.6} />
              <span className="hidden sm:inline">Cart</span>
              <span
                data-testid="cart-count"
                className="absolute -top-1 -right-1 bg-gold text-espresso text-[9px] rounded-full w-4 h-4 flex items-center justify-center font-semibold"
              >
                {count}
              </span>
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-line bg-cream">
            <div className="container-nc py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                  className="py-3 border-b border-line text-espresso"
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
};
